import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { rpc, useRpcEvent } from '../api/rpc'
import { useSession } from '../api/session'
import { Button } from '../components/ui/Button'
import { Badge, EmptyState } from '../components/ui/Card'
import { Field, Input } from '../components/ui/Field'
import { Select } from '../components/ui/Select'
import { Modal } from '../components/ui/Modal'
import { confirmDialog } from '../components/ui/Confirm'
import { useToast } from '../components/ui/Toast'
import { Cell, Count, ModalFooter, Pager, Row, RowMenu, SearchBox, Table, Td, Th, Toolbar, useTable } from '../components/ui/DataTable'
import type { MenuItem } from '../components/ui/Popover'
import { formatDateTime } from '../lib/format'

type Role = 'owner' | 'editor' | 'viewer'
const ROLES: Role[] = ['owner', 'editor', 'viewer']
interface Team { id: string; name: string; slug: string; role: Role; workspaceId: string; memberCount: number; createdAt: number }
interface Member { userId: string; name: string; email: string; role: Role }

const teamSearch = (team: Team): string => `${team.name} ${team.slug}`

/** Panel header: eyebrow, title, one-line lede, status line and the primary action (composed locally). */
function PanelHead({ eyebrow, title, lede, status, action }: {
  eyebrow: string; title: string; lede: string; status: ReactNode; action?: ReactNode
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-6 border-b border-line pb-5">
      <div className="min-w-0">
        <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-500">{eyebrow}</div>
        <h1 className="mt-2 font-display text-[30px] font-bold leading-tight tracking-tight">{title}</h1>
        <p className="mt-2 max-w-[70ch] text-sm text-ink-400">{lede}</p>
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13.5px] text-ink-400">{status}</p>
      </div>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </header>
  )
}

/** Members panel of a team: role edits, removal and the owners' add-member row. */
function TeamMembers({ team, owner }: { team: Team; owner: boolean }) {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useSession()
  const [members, setMembers] = useState<Member[]>([])
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('editor')
  const fail = useCallback((err: unknown) => toast((err as Error).message, 'error'), [toast])
  const load = useCallback(
    () => rpc.call<{ items: Member[] }>('teams.members', { teamId: team.id }).then(r => setMembers(r.items)).catch(fail),
    [team.id, fail]
  )
  useEffect(() => { void load() }, [load])
  useRpcEvent('teams.updated', (p: { teamId: string }) => { if (p.teamId === team.id) void load() })

  const changeRole = async (userId: string, next: Role) => {
    try { setMembers((await rpc.call<{ items: Member[] }>('teams.setRole', { teamId: team.id, userId, role: next })).items) } catch (err) { fail(err) }
  }
  const add = async () => {
    try { setMembers((await rpc.call<{ items: Member[] }>('teams.addMember', { teamId: team.id, email, role })).items); setEmail('') } catch (err) { fail(err) }
  }
  const remove = async (m: Member) => {
    const self = m.userId === user?.id
    if (!await confirmDialog(self ? t('teams.confirmLeave', { name: team.name }) : t('teams.confirmRemove', { name: m.name }))) return
    try { await rpc.call('teams.removeMember', { teamId: team.id, userId: m.userId }); void load() } catch (err) { fail(err) }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.12em] text-ink-500">
        <span>{t('teams.detailMembers', { count: members.length })}</span>
        <span className="h-px flex-1 bg-cream-200" />
      </div>
      {members.length === 0 ? (
        <p className="text-sm text-ink-400">{t('teams.membersEmpty')}</p>
      ) : (
        <Table minWidth={520}>
          <thead>
            <tr>
              <Th rail />
              <Th>{t('teams.colMembers')}</Th>
              <Th>{t('teams.role')}</Th>
              <Th><span className="sr-only">{t('common.actions')}</span></Th>
            </tr>
          </thead>
          <tbody>
            {members.map(m => (
              <Row key={m.userId} rail={m.role === 'owner' ? 'ok' : undefined}>
                <Td><Cell title={m.name} sub={m.email} /></Td>
                <Td>
                  {owner ? (
                    <div className="w-40">
                      <Select
                        label={t('teams.role')} value={m.role}
                        onChange={v => void changeRole(m.userId, v)}
                        options={ROLES.map(r => ({ value: r, label: t(`teams.role_${r}`) }))}
                      />
                    </div>
                  ) : <Badge>{t(`teams.role_${m.role}`)}</Badge>}
                </Td>
                <Td>
                  {(owner || m.userId === user?.id) && (
                    <RowMenu items={[{ label: m.userId === user?.id ? t('teams.leave') : t('common.remove'), danger: true, onSelect: () => void remove(m) }]} />
                  )}
                </Td>
              </Row>
            ))}
          </tbody>
        </Table>
      )}
      {owner && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[14rem] flex-1">
            <Field label={t('teams.memberEmail')}>
              {id => <Input id={id} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.com" />}
            </Field>
          </div>
          <div className="w-40">
            <Field label={t('teams.role')}>
              {id => (
                <Select
                  id={id} value={role} onChange={v => setRole(v)}
                  options={ROLES.map(r => ({ value: r, label: t(`teams.role_${r}`) }))}
                />
              )}
            </Field>
          </div>
          <Button onClick={() => void add()} disabled={!email.trim()}>{t('teams.addMember')}</Button>
        </div>
      )}
    </div>
  )
}

/** Team detail popup: rename (owners), members table and the add-member row. */
function TeamDetail({ team, onClose, onChanged }: { team: Team; onClose(): void; onChanged(): void }) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [name, setName] = useState(team.name)
  const owner = team.role === 'owner'
  const fail = useCallback((err: unknown) => toast((err as Error).message, 'error'), [toast])
  useEffect(() => { setName(team.name) }, [team.name])

  const commitName = async () => {
    const next = name.trim()
    if (!next || next === team.name) { setName(team.name); return }
    try { await rpc.call('teams.rename', { teamId: team.id, name: next }); onChanged() } catch (err) { fail(err); setName(team.name) }
  }
  const deleteTeam = async () => {
    if (!await confirmDialog(t('teams.confirmDelete', { name: team.name }))) return
    try { await rpc.call('teams.delete', { teamId: team.id }); onClose(); onChanged() } catch (err) { fail(err) }
  }

  return (
    <Modal open wide title={team.name} description={`team/${team.slug}`} onClose={onClose}
      footer={<>
        {owner && <Button variant="danger" className="sm:mr-auto" onClick={() => void deleteTeam()}>{t('common.delete')}</Button>}
        <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
      </>}>
      <div className="space-y-5">
        <dl className="grid grid-cols-[190px_1fr] gap-x-5 gap-y-3 text-sm max-sm:grid-cols-1 max-sm:gap-y-1">
          <dt className="text-ink-500">{t('teams.name')}</dt>
          <dd className="font-medium">
            {owner ? (
              <>
                <Input aria-label={t('teams.name')} value={name} className="max-w-[280px]"
                  onChange={e => setName(e.target.value)}
                  onBlur={() => void commitName()}
                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }} />
                <span className="mt-1 block text-xs text-ink-500">{t('teams.renameHint')}</span>
              </>
            ) : team.name}
          </dd>
          <dt className="text-ink-500">{t('teams.workspacePath')}</dt>
          <dd className="font-mono text-[13px]">
            {`team/${team.slug}`}
            <span className="mt-0.5 block font-sans text-xs text-ink-500">{t('teams.workspacePathHint')}</span>
          </dd>
          <dt className="text-ink-500">{t('teams.yourRole')}</dt>
          <dd><Badge tone={owner ? 'accent' : 'neutral'}>{t(`teams.role_${team.role}`)}</Badge></dd>
          <dt className="text-ink-500">{t('teams.created')}</dt>
          <dd>{formatDateTime(team.createdAt, i18n.language)}</dd>
        </dl>
        <TeamMembers team={team} owner={owner} />
      </div>
    </Modal>
  )
}

export function TeamsPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useSession()
  const [teams, setTeams] = useState<Team[] | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const fail = useCallback((err: unknown) => toast((err as Error).message, 'error'), [toast])
  const load = useCallback(() => rpc.call<{ items: Team[] }>('teams.list').then(r => setTeams(r.items)).catch(fail), [fail])
  useEffect(() => { void load() }, [load])
  useRpcEvent('teams.updated', () => void load())

  const table = useTable(teams, { search: teamSearch })
  const detail = teams?.find(x => x.id === detailId) ?? null
  const owned = teams?.filter(x => x.role === 'owner').length ?? 0
  const totalMembers = teams?.reduce((n, x) => n + x.memberCount, 0) ?? 0

  const create = async () => {
    try { await rpc.call('teams.create', { name: newName }); setNewName(''); setCreating(false); void load() } catch (err) { fail(err) }
  }
  const leave = async (team: Team) => {
    if (!user || !await confirmDialog(t('teams.confirmLeave', { name: team.name }))) return
    try { await rpc.call('teams.removeMember', { teamId: team.id, userId: user.id }); void load() } catch (err) { fail(err) }
  }
  const menu = (team: Team): MenuItem[] => [
    { label: t('common.details'), onSelect: () => setDetailId(team.id) },
    ...(team.role === 'owner' ? [{ label: t('teams.addMember'), onSelect: () => setDetailId(team.id) }] : []),
    { label: t('teams.leave'), danger: true, divider: true, onSelect: () => void leave(team) }
  ]

  return (
    <div className="space-y-5">
      <PanelHead
        eyebrow={`${t('settings.group.workspace')} / ${t('settings.nav.teams')}`}
        title={t('teams.title')}
        lede={t('teams.subtitle')}
        status={<>
          <b>{owned}</b> {t('teams.role_owner')}
          <span className="text-ink-500">·</span>
          <b>{t('teams.memberCount', { count: totalMembers })}</b>
        </>}
        action={<Button onClick={() => setCreating(true)}>{t('teams.create')}</Button>}
      />
      <Toolbar>
        <SearchBox value={table.query} onChange={table.setQuery} placeholder={t('teams.searchPlaceholder')} />
        <Count>{t('teams.count', { count: table.total })}</Count>
      </Toolbar>
      {teams?.length === 0 ? (
        <EmptyState title={t('teams.empty')} body={t('teams.emptyBody')} />
      ) : (
        <Table footer={<>
          <span className="text-xs">{t('teams.rolesHelp')}</span>
          <span className="ml-auto"><Pager page={table.page} pages={table.pages} onChange={table.setPage} /></span>
        </>}>
          <thead>
            <tr>
              <Th rail />
              <Th>{t('teams.title')}</Th>
              <Th>{t('teams.yourRole')}</Th>
              <Th className="text-right">{t('teams.colMembers')}</Th>
              <Th>{t('teams.colWorkspace')}</Th>
              <Th><span className="sr-only">{t('common.actions')}</span></Th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map(team => (
              <Row key={team.id} rail={team.role === 'owner' ? 'ok' : 'idle'} onClick={() => setDetailId(team.id)}>
                <Td><Cell title={team.name} sub={`team/${team.slug}`} /></Td>
                <Td><Badge tone={team.role === 'owner' ? 'accent' : 'neutral'}>{t(`teams.role_${team.role}`)}</Badge></Td>
                <Td className="text-right font-mono tabular-nums">{team.memberCount}</Td>
                <Td className="font-mono text-xs text-ink-400">{`team/${team.slug}`}</Td>
                <Td><RowMenu items={menu(team)} /></Td>
              </Row>
            ))}
          </tbody>
        </Table>
      )}
      {detail && <TeamDetail team={detail} onClose={() => setDetailId(null)} onChanged={() => void load()} />}
      <Modal open={creating} title={t('teams.createTitle')} description={t('teams.createHint')} onClose={() => setCreating(false)}
        footer={<ModalFooter onCancel={() => setCreating(false)} onSubmit={() => void create()} submitLabel={t('teams.create')} disabled={!newName.trim()} />}>
        <Field label={t('teams.name')}>
          {id => <Input id={id} value={newName} onChange={e => setNewName(e.target.value)} placeholder={t('teams.namePlaceholder')} />}
        </Field>
      </Modal>
    </div>
  )
}
